import {i18n} from '@online-offer/i18n';
import {
  Box,
  Device,
  Grid,
  GridItem,
  Heading,
  HStack,
  Image,
  Integer,
  Show,
  Space,
  Stack,
  Text,
  ThemeIconKey,
  useDevice,
  VStack,
} from 'platform/foundation';
import {useDateTimeFormatter, useFormatNumber} from 'platform/locale';
import {match} from 'ts-pattern';

import {lazy, Suspense} from 'react';

import {always, any, values} from 'ramda';
import {isNilOrEmpty, isNotNilOrEmpty, isValidNumber} from 'ramda-adjunct';

import {Nullish} from 'shared/web';

import {Button} from '../../components/Button/Button';
import {CardOo} from '../../components/CardOo/CardOo';
import {DataColumn, DataColumnItem} from '../../components/DataColumn/DataColumn';
import {Section} from '../../components/Section/Section';
import {Separator} from '../../components/Separator/Separator';
import {useGetOnlineOfferData} from '../../hooks/useGetOnlineOfferData';
import {ElectricalConsumptionData} from '../../utils/getFuelConsumptionData';
import {
  getVehicleConsumptionVariant,
  VehicleConsumptionVariant,
} from '../../utils/getVehicleConsumptionVariant';
import {BadgeLetter, getBadgeLetter} from '../Specification/utils/getBadgeLetter';
import {ElectricalItem} from './components/ElectricalItem';
import {getElectricalParameters} from './utils/getElectricalParameters';

const BatteryScore = lazy(() =>
  import('../../components/BatteryScore/BatteryScore').then((module) => ({
    default: module.BatteryScore,
  }))
);

const BATTERY_SCORE_SIZE = 180;

const BatteryScoreFallback = () => <Box width={BATTERY_SCORE_SIZE} height={BATTERY_SCORE_SIZE} />;

const getCarImageSrcByVariant = (variant: VehicleConsumptionVariant): string =>
  match(variant)
    .with('A', always('../../assets/images/electrical-parameters-car.svg'))
    .with('B', always('../../assets/images/electrical-parameters-car-electro.svg'))
    .with('C', always('../../assets/images/electrical-parameters-car-full-hybrid.svg'))
    .with('D', always('../../assets/images/electrical-parameters-car-plug-in-hybrid.svg'))
    .exhaustive();

function GridElectricalItem({
  icon,
  categoryTitle,
  value,
  labelTooltip,
  badge,
}: {
  value: string | Nullish;
  categoryTitle: string;
  icon: ThemeIconKey;
  labelTooltip?: string | Nullish;
  badge?: BadgeLetter | Nullish;
}) {
  if (isNilOrEmpty(value)) {
    return null;
  }
  return (
    <GridItem>
      <ElectricalItem
        value={value!}
        categoryTitle={categoryTitle}
        icon={icon}
        labelTooltip={labelTooltip}
        badge={badge}
      />
    </GridItem>
  );
}

const getElectricalParamBasedOnKey = (
  key: string
): {icon: ThemeIconKey; title: string; labelTooltip?: string | Nullish} => {
  return match<string, {icon: ThemeIconKey; title: string; labelTooltip?: string | Nullish}>(key)
    .with(
      'batteryCapacity',
      always({
        icon: 'electricalParams/battery_capacity',
        title: i18n.t('electricalParameters.item.batteryCapacity'),
      })
    )
    .with(
      'batteryWarranty',
      always({
        icon: 'electricalParams/warranty',
        title: i18n.t('electricalParameters.item.batteryWarranty'),
      })
    )
    .with(
      'electricRange',
      always({
        icon: 'specification/speedometer',
        title: i18n.t('electricalParameters.item.electricRange'),
        labelTooltip: i18n.t('electricalParameters.item.electricRangeTooltip'),
      })
    )
    .with(
      'chargingPowerAc',
      always({
        icon: 'electricalParams/charging',
        title: i18n.t('electricalParameters.item.chargingPowerAc'),
        labelTooltip: i18n.t('electricalParameters.item.chargingPowerAcTooltip'),
      })
    )
    .with(
      'chargingPowerDc',
      always({
        icon: 'electricalParams/charging',
        title: i18n.t('electricalParameters.item.chargingPowerDc'),
        labelTooltip: i18n.t('electricalParameters.item.chargingPowerDcTooltip'),
      })
    )
    .with(
      'electricEnginePower',
      always({
        icon: 'electricalParams/voltage',
        title: i18n.t('electricalParameters.item.electricEnginePower'),
        labelTooltip: i18n.t('electricalParameters.item.electricEnginePowerTooltip'),
      })
    )
    .with(
      'chargingTimeAc',
      always({
        icon: 'electricalParams/connector_ac',
        title: i18n.t('electricalParameters.item.chargingTimeAc'),
        labelTooltip: i18n.t('electricalParameters.item.chargingTimeAcTooltip'),
      })
    )
    .with(
      'chargingTimeDc',
      always({
        icon: 'electricalParams/connector_dc',
        title: i18n.t('electricalParameters.item.chargingTimeDc'),
        labelTooltip: i18n.t('electricalParameters.item.chargingTimeDcTooltip'),
      })
    )
    .with(
      'batteryVoltage',
      always({
        icon: 'electricalParams/voltage',
        title: i18n.t('electricalParameters.item.batteryVoltage'),
        labelTooltip: i18n.t('electricalParameters.item.batteryVoltageTooltip'),
      })
    )
    .with(
      'carbonDioxideEmissionDischarged',
      always({
        icon: 'specification/co2',
        title: i18n.t('electricalParameters.dataColumn.co2EmissionDischarged'),
      })
    )
    .with(
      'co2ClassDischarged',
      always({
        icon: 'specification/energy_savings_leaf',
        title: i18n.t('electricalParameters.dataColumn.co2ClassDischarged'),
      })
    )
    .otherwise(() => ({
      icon: 'electricalParams/voltage',
      title: key,
    }));
};

function getElectricalConsumptionItems(
  electricalConsumptionData: ElectricalConsumptionData | Nullish
): DataColumnItem[] {
  if (!electricalConsumptionData) {
    return [];
  }

  return match(electricalConsumptionData)
    .with({schema: 'WLTP'}, (wltpData) => [
      {
        label: i18n.t('electricalParameters.consumption.wltp.combined'),
        value: wltpData.combined,
      },
      {
        label: i18n.t('electricalParameters.consumption.wltp.cityCenter'),
        value: wltpData.cityCenter,
      },
      {
        label: i18n.t('electricalParameters.consumption.wltp.outskirts'),
        value: wltpData.outskirts,
      },
      {
        label: i18n.t('electricalParameters.consumption.wltp.countryRoads'),
        value: wltpData.countryRoads,
      },
      {
        label: i18n.t('electricalParameters.consumption.wltp.highway'),
        value: wltpData.highway,
      },
      {
        label: i18n.t('electricalParameters.consumption.wltp.weightedCombined'),
        value: wltpData.weightedCombined,
      },
    ])
    .with({schema: 'NEDC'}, (nedcData) => [
      {
        label: i18n.t('electricalParameters.consumption.nedc.combined'),
        value: nedcData.combined,
      },
    ])
    .exhaustive();
}

export function ElectricalParameters() {
  const {vehicle, alphaCatalogue, vehicleCertificateFileUri} = useGetOnlineOfferData();
  const device = useDevice();
  const formatDateTime = useDateTimeFormatter();
  const formatNumber = useFormatNumber();
  const data = getElectricalParameters(vehicle, alphaCatalogue, formatDateTime, formatNumber);
  const vehicleConsumptionVariant = getVehicleConsumptionVariant(vehicle);
  const carImageSrc = getCarImageSrcByVariant(vehicleConsumptionVariant);

  if (!vehicle || !data || !any(isNotNilOrEmpty, values(data))) {
    return null;
  }

  const {electricalData, additionalElectricalData, electricalConsumptionData, hybridType} = data;

  if (
    !any(isNotNilOrEmpty, values(electricalData)) &&
    !any(isNotNilOrEmpty, values(additionalElectricalData)) &&
    !electricalConsumptionData
  ) {
    return null;
  }

  const electricalConsumptionItems = getElectricalConsumptionItems(electricalConsumptionData);

  const shouldShowBatteryConditionSection =
    isNotNilOrEmpty(vehicle.batteryCondition) ||
    vehicle.batteryCertificate === true ||
    isNotNilOrEmpty(vehicleCertificateFileUri);

  const handleDownloadCertificate = () => {
    if (isNilOrEmpty(vehicleCertificateFileUri)) {
      return;
    }
    window.open(vehicleCertificateFileUri!, '_blank');
  };

  const columns = match(device)
    .with('mobile', always(1))
    .with('tablet', always(2))
    .with('notebook', always(2))
    .otherwise(always(4));

  const additionalDataColumns = match(device)
    .with('mobile', always(1))
    .with('tablet', always(1))
    .with('notebook', always(2))
    .otherwise(always(2));

  const isTabletOrMobile = device === 'tablet' || device === 'mobile';

  const carImageDimensions = match<Device, {width: Integer; bottom: Integer; right: Integer}>(
    device
  )
    .with(
      'tablet',
      always({
        width: 80,
        bottom: 0,
        right: 0,
      })
    )
    .with(
      'notebook',
      always({
        width: 80,
        bottom: 0,
        right: 0,
      })
    )
    .otherwise(
      always({
        width: 105,
        bottom: 0,
        right: 0,
      })
    );

  return (
    <Section
      id="ELECTRICAL_PARAMETERS"
      isWithGradient={device !== 'mobile'}
      cardContainerFlagProps={
        hybridType
          ? {
              color: 'core.orange.400',
              size: 'small',
              textColor: 'inverted',
              text: hybridType,
            }
          : undefined
      }
      isWithBreakLine
    >
      <Grid columns={columns} verticalSpacing={[8, 8, 8, 10]}>
        {Object.entries(electricalData)
          .filter(([_, value]) => value !== null)
          .map(([key, value]) => {
            const electricalItem = getElectricalParamBasedOnKey(key);
            const badge =
              key === 'carbonDioxideEmissionDischarged'
                ? getBadgeLetter(vehicle.carbonDioxideEmissionDischarged)
                : null;
            return (
              <GridElectricalItem
                key={key}
                value={value}
                categoryTitle={electricalItem.title}
                icon={electricalItem.icon}
                labelTooltip={electricalItem.labelTooltip}
                badge={badge}
              />
            );
          })}
      </Grid>
      <Show when={shouldShowBatteryConditionSection}>
        <Space vertical={8} />
        <Box paddingRight={[0, 8]}>
          <CardOo
            paddingVertical={8}
            paddingHorizontal={[6, 8]}
            backgroundColor="semantic.bg.subtle"
          >
            <Stack direction={['column', 'row']} spacing={[6, 12]} align="center">
              <Show
                when={
                  isNotNilOrEmpty(vehicle.batteryCondition) &&
                  isValidNumber(Number(vehicle.batteryCondition))
                }
              >
                <Suspense fallback={<BatteryScoreFallback />}>
                  <BatteryScore percentage={Number(vehicle.batteryCondition!)} />
                </Suspense>
              </Show>
              <VStack spacing={6} justify="center" align={['center', 'flex-start']}>
                <VStack maxWidth={105} width="100%" spacing={4} align={['center', 'flex-start']}>
                  <Heading size={3}>
                    {i18n.t('electricalParameters.batteryCondition.title')}
                  </Heading>
                  <Text size="small" color="secondary" align={['center', 'left']}>
                    {i18n.t('electricalParameters.batteryCondition.description')}
                  </Text>
                </VStack>
                <Show when={isNotNilOrEmpty(vehicleCertificateFileUri)}>
                  <HStack>
                    <Button
                      leftIcon="file/download"
                      title={i18n.t('electricalParameters.batteryCondition.downloadCertificate')}
                      variant="inverted"
                      onClick={handleDownloadCertificate}
                    />
                  </HStack>
                </Show>
              </VStack>
            </Stack>
          </CardOo>
        </Box>
        <Space vertical={8} />
        <Box paddingRight={[0, 8]}>
          <Separator />
        </Box>
      </Show>

      <Space vertical={8} />
      <Grid columns={additionalDataColumns}>
        <GridItem>
          <DataColumn
            data={[
              {
                label: i18n.t('electricalParameters.dataColumn.rangeExtender'),
                value:
                  additionalElectricalData.hasRangeExtender === true
                    ? i18n.t('general.labels.yes')
                    : null,
              },
              {
                label: i18n.t('electricalParameters.dataColumn.batteryType'),
                value: additionalElectricalData.batteryType,
              },
              {
                label: i18n.t('electricalParameters.dataColumn.chargingConnectorType'),
                value: additionalElectricalData.chargingConnectorType,
              },
              {
                label: i18n.t('electricalParameters.dataColumn.numberOfBatteries'),
                value: additionalElectricalData.numberOfBatteries,
              },
              {
                label: i18n.t('electricalParameters.dataColumn.batteryOwnershipType'),
                value: additionalElectricalData.batteryOwnershipType,
                labelTooltip: i18n.t('electricalParameters.dataColumn.batteryOwnershipTypeTooltip'),
              },
              {
                label: i18n.t('electricalParameters.dataColumn.biDirectionalCharging'),
                value: additionalElectricalData.biDirectionalCharging,
                labelTooltip: i18n.t(
                  'electricalParameters.dataColumn.biDirectionalChargingTooltip'
                ),
              },
              {
                label: i18n.t('electricalParameters.dataColumn.actualBatteryRange'),
                value: additionalElectricalData.actualBatteryRange,
              },
              ...electricalConsumptionItems,
            ]}
          />
        </GridItem>
      </Grid>
      <HStack justify={['center', 'flex-end', 'flex-start']}>
        <Box
          position={isTabletOrMobile ? 'static' : 'absolute'}
          bottom={carImageDimensions.bottom}
          right={carImageDimensions.right}
          overflow="hidden"
        >
          <Image
            isLazy
            maxWidth={carImageDimensions.width}
            fit="cover"
            width="100%"
            height="100%"
            src={carImageSrc}
          />
        </Box>
      </HStack>
    </Section>
  );
}
